/**
 * Copyright (c) Microsoft. All rights reserved.
 */

import { FocusZone, FocusZoneDirection } from '@fluentui/react/lib/FocusZone';
import { ScrollablePane } from '@fluentui/react/lib/ScrollablePane';
import { Spinner, SpinnerSize } from '@fluentui/react/lib/Spinner';
import { Stack } from '@fluentui/react/lib/Stack';
import orderBy from 'lodash.orderby';
import { observer } from 'mobx-react';
import * as React from 'react';
import { defineMessages } from 'react-intl';
import { DeletePromptModal } from 'src/core/components/DeletePromptModal';
import { ImportModal } from 'src/core/components/ImportModal';
import { LuisDebouncedCollapsableSearchBox } from 'src/core/components/LuisDebouncedCollapsableSearchBox';
import { LuisText } from 'src/core/components/LuisText';
import { useGlobalState } from 'src/core/hooks/useGlobalState';
import { useLocalization } from 'src/core/hooks/useLocalization';
import { logQnaKBButtonClick } from 'src/core/services/telemetry/eventsNamesTemplate';
import { getRandomNumber } from 'src/core/utils/randomValueGenerators';
import { getPortalFromUrl } from 'src/core/utils/routingUtils';
import { substringSearchStrategy } from 'src/core/utils/searchStrategies/substringSearchStrategy';
import { QnaMakerAddSynonymGroupModal } from 'src/qnaMaker/appShell/qnaMakerEditPage/components/QnaMakerAddSynonymGroupModal';
import { QnaMakerSynonymCard } from 'src/qnaMaker/appShell/qnaMakerEditPage/components/QnaMakerSynonymCard';
import { QnaMakerSynonymGroupCommandBar } from 'src/qnaMaker/appShell/qnaMakerEditPage/components/QnaMakerSynonymGroupCommandBar';
import { QnaMakerSynonymsUiStore } from 'src/qnaMaker/appShell/qnaMakerEditPage/QnaMakerSynonymsUiStore';
import { useSynonymsActions } from 'src/qnaMaker/modules/hooks/useSynonymsActions';

type QnaMakerSynonymListProps = {
    synonymUiStore: QnaMakerSynonymsUiStore;
    setSynonymsCount: (count: number) => void;
};

const messages = defineMessages({
    emptyStateMessage: {
        id: 'qnaMaker.qnaMakerSynonymList.emptyStateMessage',
        defaultMessage: 'No synonym groups. Select add to create a new synonym group'
    },
    loadingKnowledgeBaseFailedTitle: {
        id: 'qnaMaker.qnaMakerSynonymList.loadingKnowledgeBaseFailedTitle',
        defaultMessage: 'Failed to load synonym groups'
    },
    deleteSynonymsModalTitle: {
        id: 'qnaMaker.qnaMakerSynonymList.deleteSynonymsModalTitle',
        defaultMessage: 'Delete selected synonym group(s)'
    },
    deleteSynonymsModalBody: {
        id: 'qnaMaker.qnaMakerSynonymList.deleteSynonymsModalBody',
        defaultMessage: 'Are you sure you want to delete the selected synonym group(s)?'
    },
    importModalTitle: {
        id: 'qnaMaker.qnaMakerSynonymList.importModalTitle',
        defaultMessage: 'Import Synonym Groups'
    },
    searchPlaceholder: {
        id: 'qnaMaker.qnaMakerSynonymList.searchPlaceholder',
        defaultMessage: 'Search synonyms'
    }
});

export const QnaMakerSynonymList = observer((props: QnaMakerSynonymListProps) => {
    const { synonymUiStore, setSynonymsCount } = props;
    const { rootStore } = useGlobalState();
    const { localize } = useLocalization();
    const { serviceName } = getPortalFromUrl();

    const {
        SynonymsJson,
        displayedSynonymStores,
        searchQuery,
        isAddSynonymOn,
        savingState,
        deleteModalSettings,
        importProjectModalSettings
    } = synonymUiStore;

    const { exportSynonyms, importSynonyms, deleteSynonym, addSynonym } = useSynonymsActions(synonymUiStore);

    const { areSynonymsLoaded, id: currentProjectId } = rootStore.currentQnaMakerProjectStore;

    const isInEmptyState = areSynonymsLoaded && !SynonymsJson.length;

    const [isAddSynonymModalHidden, setIsAddSynonymModalHidden] = React.useState(true);

    const filteredSynonyms = React.useMemo(
        () => orderBy(substringSearchStrategy(displayedSynonymStores, searchQuery, ['alterations']), ['id'], ['desc']),

        [searchQuery, SynonymsJson, displayedSynonymStores]
    );

    const orderedSynonyms = React.useMemo(() => filteredSynonyms.sort((syn1, syn2) => (syn1.id > syn2.id ? 1 : -1)), [filteredSynonyms]);

    React.useEffect(() => setSynonymsCount(orderedSynonyms.length), [orderedSynonyms]);

    React.useEffect(() => {
        if (
            !Boolean(synonymUiStore.selectedSynonymId) ||
            displayedSynonymStores.findIndex(s => s.id === synonymUiStore.selectedSynonymId) === -1
        ) {
            synonymUiStore.setSelectedSynonymId(orderedSynonyms[0]?.id);
        }
    }, [orderedSynonyms]);

    const logCommandbarClick = (prop: string) => logQnaKBButtonClick(serviceName, 'SynonymsCommandbar', 'Button', prop, currentProjectId);

    const handleAddSynonym = () => {
        logCommandbarClick('AddSynonym');
        synonymUiStore.setIsAddSynonymOn(true);
        setIsAddSynonymModalHidden(false);
    };

    const handleImport = (fileType: 'tsv' | 'xlsx') => {
        logCommandbarClick(fileType === 'xlsx' ? 'ImportExcel' : 'ImportTsv');
        synonymUiStore.setImportProjectModalSettings({ hidden: false, fileType });
    };

    const handleExport = (fileType: 'tsv' | 'xlsx') => {
        logCommandbarClick(fileType === 'xlsx' ? 'ExportExcel' : 'ExportTsv');
        exportSynonyms(fileType);
    };

    const handleDeleteSynonym = () => {
        logCommandbarClick('DeleteSynonym');
        synonymUiStore.setDeleteModalSettings({ hidden: false, operation: 'synonym' });
    };

    const handleSubmitAddSynonym = (alterations: string[]) => {
        logQnaKBButtonClick(serviceName, 'AddSynonymModal', 'Button', 'Add', currentProjectId);
        const id = getRandomNumber();
        addSynonym({ id, alterations });
        setIsAddSynonymModalHidden(true);
        synonymUiStore.setSelectedSynonymId(id);
    };

    const handleDismissAddSynonym = () => {
        logQnaKBButtonClick(serviceName, 'AddSynonymModal', 'Button', 'Cancel', currentProjectId);
        setIsAddSynonymModalHidden(true);
        synonymUiStore.setIsAddSynonymOn(false);
    };

    const handleSubmitDeleteSynonym = () => {
        logQnaKBButtonClick(serviceName, 'DeleteSynonymModal', 'Button', 'Confirm', currentProjectId);
        const idx = orderedSynonyms.findIndex(s => s.id === synonymUiStore.selectedSynonymId);
        const newIdx = idx === orderedSynonyms.length - 1 ? 0 : idx + 1;
        deleteSynonym([synonymUiStore.selectedSynonymId]);
        if (orderedSynonyms.length) {
            synonymUiStore.setSelectedSynonymId(orderedSynonyms[newIdx].id);
        }
    };

    const deleteModalRenderer = () => {
        const title = localize(messages.deleteSynonymsModalTitle);
        const body = localize(messages.deleteSynonymsModalBody);

        return (
            <DeletePromptModal
                dialogContentProps={{ title }}
                hidden={deleteModalSettings.hidden}
                onDelete={handleSubmitDeleteSynonym}
                onDismiss={() => {
                    logQnaKBButtonClick(serviceName, 'DeleteSynonymModal', 'Button', 'Cancel', currentProjectId);
                    synonymUiStore.setDeleteModalSettings({ hidden: true, operation: null });
                }}>
                {body}
            </DeletePromptModal>
        );
    };

    return (
        <Stack verticalFill tokens={{ childrenGap: 10 }}>
            <ScrollablePane styles={{ root: { height: '100%', position: 'relative' } }}>
                <QnaMakerSynonymGroupCommandBar
                    onExport={handleExport}
                    isAddSynonymOn={isAddSynonymOn}
                    onAddNewSynonym={handleAddSynonym}
                    isInDirtyState={savingState !== 'saved'}
                    isInEmptyState={synonymUiStore.SynonymsJson.length === 0}
                    areEditActionsDisabled={synonymUiStore.savingState === 'inProgress'}
                    onImport={handleImport}
                    onDelete={handleDeleteSynonym}
                    importProjectModalSettings={importProjectModalSettings}
                />
                {isInEmptyState ? (
                    <Stack styles={{ root: { width: '100%', paddingTop: '50px' } }}>
                        <LuisText styles={{ root: { textAlign: 'center', alignSelf: 'center', width: '80%' } }}>
                            {localize(messages.emptyStateMessage)}
                        </LuisText>
                    </Stack>
                ) : (
                    <>
                        <Stack horizontal tokens={{ padding: '10px 5px' }} horizontalAlign={areSynonymsLoaded ? 'end' : 'space-between'}>
                            {!areSynonymsLoaded && <Spinner size={SpinnerSize.medium} />}
                            <LuisDebouncedCollapsableSearchBox
                                alwaysExpanded
                                autoFocus={false}
                                ariaLabel={localize(messages.searchPlaceholder)}
                                size={40}
                                placeholder={localize(messages.searchPlaceholder)}
                                value={searchQuery}
                                onChange={(_, newValue?: string) => synonymUiStore.setSearchQuery(newValue)}
                            />
                        </Stack>
                        <FocusZone
                            direction={FocusZoneDirection.vertical}
                            defaultTabbableElement={`#synonym${props.synonymUiStore.selectedSynonymId}`}>
                            {orderedSynonyms.map(syn => (
                                <QnaMakerSynonymCard
                                    synData={syn}
                                    isSelected={props.synonymUiStore.selectedSynonymId === syn.id}
                                    setIsSelected={() => props.synonymUiStore.setSelectedSynonymId(syn.id)}
                                />
                            ))}
                        </FocusZone>
                    </>
                )}
            </ScrollablePane>

            {deleteModalRenderer()}

            <ImportModal
                isNameFieldDisplayed={false}
                hidden={importProjectModalSettings.hidden}
                fileType={importProjectModalSettings.fileType}
                dialogContentProps={{ title: localize(messages.importModalTitle) }}
                onDismiss={() => synonymUiStore.setImportProjectModalSettings({ hidden: true })}
                onSubmit={data => {
                    importSynonyms({ ...data, fileType: importProjectModalSettings.fileType });
                    synonymUiStore.setImportProjectModalSettings({ hidden: true });
                }}
            />

            <QnaMakerAddSynonymGroupModal
                hidden={isAddSynonymModalHidden}
                onDismiss={handleDismissAddSynonym}
                onSubmitSynonym={handleSubmitAddSynonym}
            />
        </Stack>
    );
});
